import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Modal,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';

import { useTheme } from '../../theme/ThemeContext';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { Button } from '../common/Button';
import type { AppDispatch } from '../../store';

import { logger } from '../../utils/logger';
import {
  createBalancedGroupChallenge,
  selectGroupChallenges,
  selectActiveGroupChallenges,
  selectStudyGroupLoading,
  selectStudyGroupError,
  updateChallengeProgress,
  type GroupChallenge,
  type ChallengeReward,
} from '../../store/slices/studyGroupSlice';

interface GroupChallengeCreatorProps {
  groupId: string;
  memberSkills: Record<string, number>;
  onChallengeCreated?: (challenge: GroupChallenge) => void;
  onChallengeStart?: (challengeId: string) => void;
}

export const GroupChallengeCreator: React.FC<GroupChallengeCreatorProps> = ({
  groupId,
  memberSkills,
  onChallengeCreated,
  onChallengeStart,
}) => {
  const { theme } = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const challenges = useSelector(selectGroupChallenges);
  const activeChallenges = useSelector(selectActiveGroupChallenges);
  const loading = useSelector(selectStudyGroupLoading);
  const error = useSelector(selectStudyGroupError);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedChallengeType, setSelectedChallengeType] =
    useState<GroupChallenge['type']>('brewing_challenge');
  const [challengeDuration, setChallengeDuration] = useState(60);
  const [challengeObjectives, setChallengeObjectives] = useState<string[]>([]);
  const [newObjective, setNewObjective] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const challengeTypes = [
    {
      type: 'brewing_challenge' as const,
      title: 'Brewing Challenge',
      description: 'Practice brewing techniques with group feedback',
      icon: 'cafe',
      difficulty: 0.6,
      estimatedDuration: 45,
    },
    {
      type: 'knowledge_quiz' as const,
      title: 'Knowledge Quiz',
      description: 'Test coffee knowledge in a collaborative format',
      icon: 'help-circle',
      difficulty: 0.4,
      estimatedDuration: 30,
    },
    {
      type: 'skill_practice' as const,
      title: 'Skill Practice',
      description: 'Focus on specific coffee skills with peer support',
      icon: 'fitness',
      difficulty: 0.7,
      estimatedDuration: 60,
    },
    {
      type: 'creative_project' as const,
      title: 'Creative Project',
      description: 'Collaborative coffee-related creative projects',
      icon: 'color-palette',
      difficulty: 0.8,
      estimatedDuration: 90,
    },
  ];

  const handleCreateChallenge = async () => {
    if (challengeObjectives.length === 0) {
      Alert.alert('Error', 'Please add at least one learning objective');
      return;
    }

    try {
      setIsCreating(true);
      const challenge = await dispatch(
        createBalancedGroupChallenge({
          groupId,
          challengeType: selectedChallengeType,
          memberSkills,
          duration: challengeDuration,
          objectives: challengeObjectives,
        })
      ).unwrap();

      setShowCreateModal(false);
      resetForm();
      onChallengeCreated?.(challenge);
      Alert.alert(
        'Success',
        'AI-balanced group challenge created successfully!'
      );
    } catch (err) {
      Alert.alert(
        'Error',
        'Failed to create group challenge. Please try again.'
      );
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setSelectedChallengeType('brewing_challenge');
    setChallengeDuration(60);
    setChallengeObjectives([]);
    setNewObjective('');
  };

  const addObjective = () => {
    if (
      newObjective.trim() &&
      !challengeObjectives.includes(newObjective.trim())
    ) {
      setChallengeObjectives([...challengeObjectives, newObjective.trim()]);
      setNewObjective('');
    }
  };

  const removeObjective = (index: number) => {
    setChallengeObjectives(challengeObjectives.filter((_, i) => i !== index));
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 0.3) return theme.colors.success;
    if (difficulty <= 0.7) return theme.colors.warning;
    return theme.colors.error;
  };

  const getDifficultyLabel = (difficulty: number) => {
    if (difficulty <= 0.3) return 'Easy';
    if (difficulty <= 0.7) return 'Medium';
    return 'Hard';
  };

  const getStatusColor = (status: GroupChallenge['status']) => {
    switch (status) {
      case 'active':
        return theme.colors.success;
      case 'upcoming':
        return theme.colors.primary;
      case 'completed':
        return theme.colors.textSecondary;
      case 'cancelled':
        return theme.colors.error;
      default:
        return theme.colors.textSecondary;
    }
  };

  const formatTimeRemaining = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  const renderChallengeItem = ({ item }: { item: GroupChallenge }) => {
    const statusColor = getStatusColor(item.status);
    const difficultyColor = getDifficultyColor(item.difficulty);

    return (
      <View
        style={[
          styles.challengeCard,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        {/* Header */}
        <View style={styles.challengeHeader}>
          <View style={styles.challengeInfo}>
            <View style={styles.challengeTitleRow}>
              <Text
                style={[styles.challengeTitle, { color: theme.colors.text }]}
              >
                {item.title}
              </Text>
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: statusColor + '20' },
                ]}
              >
                <Text style={[styles.statusText, { color: statusColor }]}>
                  {item.status}
                </Text>
              </View>
            </View>
            <Text
              style={[
                styles.challengeDescription,
                { color: theme.colors.textSecondary },
              ]}
            >
              {item.description}
            </Text>
          </View>
        </View>

        {/* Challenge Stats */}
        <View style={styles.challengeStats}>
          <View style={styles.statItem}>
            <Ionicons
              name='time-outline'
              size={16}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.statText, { color: theme.colors.textSecondary }]}
            >
              {formatTimeRemaining(item.duration)}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Ionicons
              name='people-outline'
              size={16}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.statText, { color: theme.colors.textSecondary }]}
            >
              {item.currentParticipants}/{item.maxParticipants}
            </Text>
          </View>
          <View style={styles.statItem}>
            <View
              style={[
                styles.difficultyDot,
                { backgroundColor: difficultyColor },
              ]}
            />
            <Text style={[styles.statText, { color: difficultyColor }]}>
              {getDifficultyLabel(item.difficulty)}
            </Text>
          </View>
        </View>

        {/* Progress */}
        {item.status === 'active' && (
          <View style={styles.challengeProgress}>
            <View style={styles.progressHeader}>
              <Text
                style={[
                  styles.progressLabel,
                  { color: theme.colors.textSecondary },
                ]}
              >
                Progress
              </Text>
              <Text
                style={[styles.progressValue, { color: theme.colors.text }]}
              >
                {Math.round(item.progress.overallProgress * 100)}%
              </Text>
            </View>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  {
                    backgroundColor: theme.colors.primary,
                    width: `${item.progress.overallProgress * 100}%`,
                  },
                ]}
              />
            </View>
            {item.progress.timeRemaining > 0 && (
              <Text
                style={[
                  styles.timeRemaining,
                  { color: theme.colors.textSecondary },
                ]}
              >
                {formatTimeRemaining(item.progress.timeRemaining)} remaining
              </Text>
            )}
          </View>
        )}

        {/* AI Optimization Badge */}
        {item.aiOptimized && (
          <View
            style={[
              styles.aiOptimizedBadge,
              { backgroundColor: theme.colors.primaryLight },
            ]}
          >
            <Ionicons name='bulb' size={14} color={theme.colors.primary} />
            <Text
              style={[styles.aiOptimizedText, { color: theme.colors.primary }]}
            >
              AI-balanced for group skills
            </Text>
          </View>
        )}

        {/* Rewards */}
        {item.rewards.length > 0 && (
          <View style={styles.challengeRewards}>
            <Text
              style={[
                styles.rewardsLabel,
                { color: theme.colors.textSecondary },
              ]}
            >
              Rewards:
            </Text>
            <View style={styles.rewardTags}>
              {item.rewards.slice(0, 2).map((reward, index) => (
                <View
                  key={index}
                  style={[
                    styles.rewardTag,
                    { backgroundColor: theme.colors.warningLight },
                  ]}
                >
                  <Ionicons
                    name='trophy'
                    size={12}
                    color={theme.colors.warning}
                  />
                  <Text
                    style={[styles.rewardText, { color: theme.colors.warning }]}
                  >
                    {reward.description}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Actions */}
        <View style={styles.challengeActions}>
          {item.status === 'upcoming' && (
            <Button
              title='Start Challenge'
              onPress={() => onChallengeStart?.(item.id)}
              variant='primary'
              size='small'
              icon='play'
            />
          )}
          {item.status === 'active' && (
            <Button
              title='View Progress'
              onPress={() => onChallengeStart?.(item.id)}
              variant='outline'
              size='small'
              icon='analytics'
            />
          )}
          <TouchableOpacity
            style={[styles.detailsButton, { borderColor: theme.colors.border }]}
            onPress={() => {
              // Navigate to challenge details
              logger.log('View challenge details:', item.id);
            }}
          >
            <Text
              style={[
                styles.detailsButtonText,
                { color: theme.colors.primary },
              ]}
            >
              Details
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderCreateModal = () => {
    const selectedType = challengeTypes.find(
      t => t.type === selectedChallengeType
    );

    return (
      <Modal
        visible={showCreateModal}
        animationType='slide'
        presentationStyle='pageSheet'
      >
        <View
          style={[
            styles.modalContainer,
            { backgroundColor: theme.colors.background },
          ]}
        >
          <View style={styles.modalHeader}>
            <TouchableOpacity
              onPress={() => setShowCreateModal(false)}
              style={styles.modalCloseButton}
            >
              <Text
                style={[styles.modalCloseText, { color: theme.colors.primary }]}
              >
                Cancel
              </Text>
            </TouchableOpacity>
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
              Create Group Challenge
            </Text>
            <TouchableOpacity
              onPress={handleCreateChallenge}
              style={styles.modalSaveButton}
              disabled={isCreating}
            >
              <Text
                style={[
                  styles.modalSaveText,
                  {
                    color: isCreating
                      ? theme.colors.textSecondary
                      : theme.colors.primary,
                  },
                ]}
              >
                Create
              </Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            style={styles.modalContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Challenge Type Selection */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                Challenge Type
              </Text>
              {challengeTypes.map(type => (
                <TouchableOpacity
                  key={type.type}
                  style={[
                    styles.typeOption,
                    {
                      backgroundColor:
                        selectedChallengeType === type.type
                          ? theme.colors.primaryLight
                          : theme.colors.surface,
                      borderColor:
                        selectedChallengeType === type.type
                          ? theme.colors.primary
                          : theme.colors.border,
                    },
                  ]}
                  onPress={() => {
                    setSelectedChallengeType(type.type);
                    setChallengeDuration(type.estimatedDuration);
                  }}
                >
                  <Ionicons
                    name={type.icon as any}
                    size={20}
                    color={
                      selectedChallengeType === type.type
                        ? theme.colors.primary
                        : theme.colors.textSecondary
                    }
                  />
                  <View style={styles.typeInfo}>
                    <Text
                      style={[
                        styles.typeTitle,
                        {
                          color:
                            selectedChallengeType === type.type
                              ? theme.colors.primary
                              : theme.colors.text,
                        },
                      ]}
                    >
                      {type.title}
                    </Text>
                    <Text
                      style={[
                        styles.typeDescription,
                        { color: theme.colors.textSecondary },
                      ]}
                    >
                      {type.description}
                    </Text>
                  </View>
                  <View style={styles.typeMeta}>
                    <Text
                      style={[
                        styles.typeDifficulty,
                        { color: getDifficultyColor(type.difficulty) },
                      ]}
                    >
                      {getDifficultyLabel(type.difficulty)}
                    </Text>
                    <Text
                      style={[
                        styles.typeDuration,
                        { color: theme.colors.textSecondary },
                      ]}
                    >
                      {type.estimatedDuration}m
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </View>

            {/* Duration */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                Duration (minutes)
              </Text>
              <View style={styles.durationSelector}>
                {[30, 45, 60, 90, 120].map(duration => (
                  <TouchableOpacity
                    key={duration}
                    style={[
                      styles.durationOption,
                      {
                        backgroundColor:
                          challengeDuration === duration
                            ? theme.colors.primaryLight
                            : theme.colors.surface,
                        borderColor:
                          challengeDuration === duration
                            ? theme.colors.primary
                            : theme.colors.border,
                      },
                    ]}
                    onPress={() => setChallengeDuration(duration)}
                  >
                    <Text
                      style={[
                        styles.durationText,
                        {
                          color:
                            challengeDuration === duration
                              ? theme.colors.primary
                              : theme.colors.text,
                        },
                      ]}
                    >
                      {duration}m
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Learning Objectives */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                Learning Objectives
              </Text>
              <View style={styles.objectiveInput}>
                <TextInput
                  style={[
                    styles.textInput,
                    {
                      backgroundColor: theme.colors.surface,
                      color: theme.colors.text,
                      borderColor: theme.colors.border,
                    },
                  ]}
                  value={newObjective}
                  onChangeText={setNewObjective}
                  placeholder='Add a learning objective...'
                  placeholderTextColor={theme.colors.textSecondary}
                  onSubmitEditing={addObjective}
                />
                <TouchableOpacity
                  style={[
                    styles.addButton,
                    { backgroundColor: theme.colors.primary },
                  ]}
                  onPress={addObjective}
                >
                  <Ionicons name='add' size={20} color='white' />
                </TouchableOpacity>
              </View>
              <View style={styles.objectivesList}>
                {challengeObjectives.map((objective, index) => (
                  <View
                    key={index}
                    style={[
                      styles.objectiveItem,
                      { backgroundColor: theme.colors.surface },
                    ]}
                  >
                    <Text
                      style={[
                        styles.objectiveText,
                        { color: theme.colors.text },
                      ]}
                    >
                      {objective}
                    </Text>
                    <TouchableOpacity
                      onPress={() => removeObjective(index)}
                      style={styles.removeButton}
                    >
                      <Ionicons
                        name='close'
                        size={16}
                        color={theme.colors.error}
                      />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            </View>

            {/* AI Balancing Info */}
            <View
              style={[
                styles.aiBalancingInfo,
                { backgroundColor: theme.colors.primaryLight },
              ]}
            >
              <Ionicons name='bulb' size={20} color={theme.colors.primary} />
              <Text
                style={[
                  styles.aiBalancingText,
                  { color: theme.colors.primary },
                ]}
              >
                AI will automatically balance this challenge based on your
                group's skill levels and learning goals
              </Text>
            </View>
          </ScrollView>
        </View>
      </Modal>
    );
  };

  if (loading.challenges) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingSpinner />
        <Text
          style={[styles.loadingText, { color: theme.colors.textSecondary }]}
        >
          Loading group challenges...
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          Group Challenges
        </Text>
        <Button
          title='Create Challenge'
          onPress={() => setShowCreateModal(true)}
          variant='primary'
          size='small'
          icon='add'
        />
      </View>

      {/* Error Display */}
      {error && (
        <ErrorMessage
          message={error}
          onRetry={() => {
            // Retry logic would go here
          }}
        />
      )}

      {/* Active Challenges Summary */}
      {activeChallenges.length > 0 && (
        <View
          style={[
            styles.activeSummary,
            { backgroundColor: theme.colors.successLight },
          ]}
        >
          <Ionicons name='flash' size={20} color={theme.colors.success} />
          <Text
            style={[styles.activeSummaryText, { color: theme.colors.success }]}
          >
            {activeChallenges.length} active challenge
            {activeChallenges.length !== 1 ? 's' : ''}
          </Text>
        </View>
      )}

      {/* Challenges List */}
      <ScrollView
        style={styles.challengesList}
        showsVerticalScrollIndicator={false}
      >
        {challenges.map(challenge => (
          <View key={challenge.id}>
            {renderChallengeItem({ item: challenge })}
          </View>
        ))}
        {challenges.length === 0 && (
          <View style={styles.emptyContainer}>
            <Ionicons
              name='trophy-outline'
              size={64}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.emptyText, { color: theme.colors.textSecondary }]}
            >
              No group challenges yet
            </Text>
            <Text
              style={[
                styles.emptySubtext,
                { color: theme.colors.textSecondary },
              ]}
            >
              Create AI-balanced challenges to engage your study group
            </Text>
            <Button
              title='Create First Challenge'
              onPress={() => setShowCreateModal(true)}
              variant='primary'
              size='medium'
              icon='add'
              style={styles.createFirstButton}
            />
          </View>
        )}
      </ScrollView>

      {/* Create Challenge Modal */}
      {renderCreateModal()}

      {/* Creating Overlay */}
      {isCreating && (
        <View style={styles.creatingOverlay}>
          <View
            style={[
              styles.creatingModal,
              { backgroundColor: theme.colors.surface },
            ]}
          >
            <LoadingSpinner />
            <Text style={[styles.creatingText, { color: theme.colors.text }]}>
              AI is balancing your challenge...
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  activeSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    margin: 16,
    borderRadius: 8,
  },
  activeSummaryText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 8,
  },
  challengesList: {
    flex: 1,
    paddingHorizontal: 16,
  },
  challengeCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  challengeHeader: {
    marginBottom: 12,
  },
  challengeInfo: {
    flex: 1,
  },
  challengeTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  challengeTitle: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  challengeDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  challengeStats: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  statText: {
    fontSize: 12,
    marginLeft: 4,
  },
  difficultyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4,
  },
  challengeProgress: {
    marginBottom: 12,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 14,
  },
  progressValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  progressBar: {
    height: 4,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: 2,
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  timeRemaining: {
    fontSize: 12,
    textAlign: 'center',
  },
  aiOptimizedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
    marginBottom: 12,
  },
  aiOptimizedText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },
  challengeRewards: {
    marginBottom: 12,
  },
  rewardsLabel: {
    fontSize: 14,
    marginBottom: 8,
  },
  rewardTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  rewardTag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  rewardText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },
  challengeActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailsButton: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  detailsButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 32,
    marginBottom: 24,
  },
  createFirstButton: {
    marginTop: 16,
  },
  modalContainer: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalCloseText: {
    fontSize: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  modalSaveButton: {
    padding: 4,
  },
  modalSaveText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalContent: {
    flex: 1,
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  typeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 8,
  },
  typeInfo: {
    flex: 1,
    marginLeft: 12,
  },
  typeTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  typeDescription: {
    fontSize: 12,
    lineHeight: 16,
  },
  typeMeta: {
    alignItems: 'flex-end',
  },
  typeDifficulty: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 2,
  },
  typeDuration: {
    fontSize: 12,
  },
  durationSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  durationOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    marginRight: 8,
    marginBottom: 8,
  },
  durationText: {
    fontSize: 14,
    fontWeight: '500',
  },
  objectiveInput: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    marginRight: 8,
  },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  objectivesList: {
    marginTop: 8,
  },
  objectiveItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  objectiveText: {
    fontSize: 14,
    flex: 1,
  },
  removeButton: {
    padding: 4,
  },
  aiBalancingInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  aiBalancingText: {
    fontSize: 14,
    lineHeight: 20,
    marginLeft: 8,
    flex: 1,
  },
  creatingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  creatingModal: {
    padding: 24,
    borderRadius: 12,
    alignItems: 'center',
    minWidth: 200,
  },
  creatingText: {
    fontSize: 16,
    marginTop: 16,
    textAlign: 'center',
  },
});
